import { flowchartLogger } from 'src/lib/logger';

import { defaultEntryNode } from './defaultData';

import type {
  DialplanDetailedDataBase,
  NodeDetailed,
  FlowChartData,
  SortableInput,
  MetaDataBase,
  QueueInstruction,
  UserInstruction,
  ExtensionInstruction,
  FlowChartNodeData,
  MappingRule,
} from './types';
import type { Edge, Node } from '@xyflow/react';

interface EdgeOptionalProps {
  sourceHandle?: string;
  targetHandle?: string;
}

const defaultDialplanData: DialplanDetailedDataBase = {
  nodes: [],
  entry: defaultEntryNode(),
};

export default class FrontendData {
  private readonly data: DialplanDetailedDataBase;
  private readonly nodeAutoPosition: { x: number; y: number } = { x: 0, y: 0 };

  constructor(data: DialplanDetailedDataBase = defaultDialplanData) {
    this.data = data;
  }

  prepare(): FlowChartData {
    flowchartLogger.debug('Preparing data for frontend', { data: this.data });
    const nodes: Array<Node<FlowChartNodeData>> = [
      {
        id: 'entry',
        data: { fixedX: 0 },
        type: 'entry',
        position: { x: 0, y: 0 },
        deletable: false,
      },
    ];
    const edges: Edge[] = [];

    const { entry, nodes: callflowNodes } = this.data;

    nodes.push(this.convertNodeData(entry));

    // Entry node uses 'entry' as its Handle ID
    edges.push(this.createEdge('entry', entry.node_id, { sourceHandle: 'entry' }));
    this.processNode(entry, nodes, edges);

    callflowNodes.forEach((node) => {
      nodes.push(this.convertNodeData(node));
      this.processNode(node, nodes, edges);
    });

    flowchartLogger.debug('Prepared data for frontend', { nodes, edges });

    return {
      nodes,
      edges,
    };
  }

  convertNodeData(node: NodeDetailed): Node<FlowChartNodeData> {
    let metadata = 'metadata' in node && node.metadata !== undefined ? node.metadata : {};
    if (typeof node.metadata === 'string') {
      metadata = JSON.parse(node.metadata);
    }
    const ingordMetadata: MetaDataBase = (metadata.ingord as MetaDataBase) ?? {
      description: '',
      position: Object.assign({}, this.nodeAutoPosition),
    };
    if ('ingord' in metadata) {
      delete metadata.ingord;
    } else {
      if (this.nodeAutoPosition.x > 2000) {
        this.nodeAutoPosition.x = 0;
        this.nodeAutoPosition.y += 300;
      } else {
        this.nodeAutoPosition.x += 300;
      }
    }

    const convertedNode: Node<FlowChartNodeData> = {
      id: node.node_id,
      type: node.type,
      data: {
        metadata: node.metadata,
        description: ingordMetadata.description ?? '',
      },
      position: { x: ingordMetadata.position.x, y: ingordMetadata.position.y },
      selected: ingordMetadata.selected === true,
    };
    if (node.type === 'router' || node.type === 'custom_router') {
      const matchRules: SortableInput[] = [];
      node.match_rules.forEach((matchRule, index) => {
        matchRules.push({ id: `${node.node_id}-${index}`, value: matchRule.match_rule });
      });
      convertedNode.type = 'router';
      convertedNode.data.matchRules = matchRules;
      convertedNode.data.matchProperty = node.match_property;
    } else if (node.type === 'mapping') {
      const mappingRules: MappingRule[] = [];
      node.match_rules.forEach((matchRule) => {
        mappingRules.push({
          matchRule: matchRule.match_rule,
          replaceRule: matchRule.replace_rule,
        });
      });
      convertedNode.data.mappingRules = mappingRules;
      convertedNode.data.matchProperty = node.match_property.property;
      convertedNode.data.setProperty = node.set_property.property;
    } else if (node.type === 'calendar') {
      convertedNode.data.calendar = node.calendar;
    } else if (node.type === 'user') {
      convertedNode.data.user = node.user;
      const instructions: UserInstruction[] = [];
      if ('connections' in node && node.connections !== undefined) {
        node.connections.forEach((connection, index) => {
          instructions.push({
            id: `${node.node_id}-${index}`,
            description: connection.description,
            match_exit_causes: connection.match_exit_causes,
          });
        });
        convertedNode.data.instructions = instructions;
      }
    } else if (node.type === 'lcr') {
      convertedNode.data.user = node.user;
    } else if (node.type === 'user_voicemail') {
      convertedNode.data.user = node.user;
    } else if (node.type === 'extension') {
      const instructions: ExtensionInstruction[] = [];
      if ('connections' in node && node.connections !== undefined) {
        node.connections.forEach((connection, index) => {
          instructions.push({
            id: `${node.node_id}-${index}`,
            description: connection.description,
            match_exit_causes: connection.match_exit_causes,
          });
        });
        convertedNode.data.instructions = instructions;
      }
    } else if (node.type === 'forward') {
      convertedNode.data.number = node.number;
    } else if (node.type === 'goto') {
      convertedNode.data.goto = node.callflow;
    } else if (node.type === 'group') {
      const instructions: ExtensionInstruction[] = [];
      if ('connections' in node && node.connections !== undefined) {
        node.connections.forEach((connection, index) => {
          instructions.push({
            id: `${node.node_id}-${index}`,
            description: connection.description,
            match_exit_causes: connection.match_exit_causes,
          });
        });
        convertedNode.data.instructions = instructions;
      }
      convertedNode.data.group = node.group;
      convertedNode.data.call_timeout = node.call_timeout;
      convertedNode.type = 'groupNode';
    } else if (node.type === 'http') {
      convertedNode.data.url = node.url ?? '';
      convertedNode.data.method = node.method ?? 'get';
      convertedNode.data.headers = node.headers ?? [];
      convertedNode.data.sslVerify = node.ssl_verify ?? true;
    } else if (node.type === 'hunt') {
      convertedNode.data.enableScreening = node.enable_screening;
      convertedNode.data.parallel = node.parallel;
      convertedNode.data.huntingGroupId = node.hunting_group.id;
    } else if (node.type === 'ivr') {
      const selections: SortableInput[] = [];
      node.selections.forEach((selection, index) => {
        selections.push({ id: `${node.node_id}-${index}`, value: selection.selection });
      });
      convertedNode.data.selections = selections;
      convertedNode.data.errorSoundfile = node.error_soundfile.soundfile;
      convertedNode.data.menuSoundfile = node.menu_soundfile.soundfile;
      convertedNode.data.timeoutSoundfile = node.timeout_soundfile.soundfile;
      convertedNode.data.timeout = node.timeout ?? 1000;
      convertedNode.data.retries = node.retries ?? 0;
    } else if (node.type === 'playback') {
      if ('soundfile' in node.audio_source && node.audio_source.soundfile !== undefined) {
        convertedNode.data.audioSource = node.audio_source.soundfile;
      } else {
        convertedNode.data.url = node.audio_source.url ?? '';
        convertedNode.data.method = node.audio_source.method ?? 'get';
        convertedNode.data.headers = node.audio_source.headers ?? [];
        convertedNode.data.sslVerify = node.audio_source.ssl_verify ?? true;
      }
      if ('dtmf_gather' in node && node.dtmf_gather !== null && node.dtmf_gather !== undefined) {
        convertedNode.data.maxDigits = node.dtmf_gather.max_digits ?? 128;
        convertedNode.data.outputPropName = node.dtmf_gather.output_prop_name ?? '';
        convertedNode.data.terminators = node.dtmf_gather.terminators ?? '';
        convertedNode.data.timeout = node.dtmf_gather.timeout ?? 5000;
      }
    } else if (node.type === 'queue') {
      convertedNode.data.queue = node.queue;
      const instructions: QueueInstruction[] = [];
      if ('connections' in node && node.connections !== undefined) {
        node.connections.forEach((connection, index) => {
          instructions.push({
            id: `${node.node_id}-${index}`,
            description: connection.description,
            match_exit_causes: connection.match_exit_causes,
          });
        });
        convertedNode.data.instructions = instructions;
      }
    } else if (node.type === 'speech') {
      convertedNode.data.auth_key = node.auth_key;
      convertedNode.data.text = node.text;
      convertedNode.data.encoding = node.encoding;
      convertedNode.data.language = node.language;
    } else if (node.type === 'answer') {
      convertedNode.data.delay = node.delay ?? 0;
    } else if (node.type === 'conference') {
      convertedNode.data.conference = node.conference;
    } else if (node.type === 'disconnect') {
      convertedNode.data.reason = node.reason;
    } else if (node.type === 'distribution_list') {
      convertedNode.data.distribution_list = node.distribution_list;
    } else if (node.type === 'ai_assistant') {
      convertedNode.data.instructions = node.instructions;
      convertedNode.data.model = node.model;
      convertedNode.data.voice = node.voice;
      convertedNode.data.transcription = node.transcription;
    }

    return convertedNode;
  }

  createEdge(source: string, target: string, optionalProps: EdgeOptionalProps = {}): Edge {
    const separator = '-';
    const id =
      optionalProps.sourceHandle !== undefined
        ? `${source}${separator}${optionalProps.sourceHandle}${separator}${target}`
        : `${source}${separator}${target}`;
    const edge: Edge = {
      id,
      source,
      target,
      ...optionalProps,
    };
    return edge;
  }

  processNode(node: NodeDetailed, nodes: Array<Node<FlowChartNodeData>>, edges: Edge[]): void {
    if ('connection' in node && node.connection !== undefined && node.connection !== null) {
      // For simple connections, use node-specific handle ID
      // Entry node uses 'entry' as handle ID, others use '{type}-source'
      const sourceHandle = node.node_id === 'entry' ? 'entry' : `${node.type}-source`;
      edges.push(this.createEdge(node.node_id, node.connection.next_id, { sourceHandle }));
    }
    if ('connections' in node && node.connections !== undefined && node.connections.length > 0) {
      const processedNode: Node<FlowChartNodeData> | undefined = nodes.find((n) => n.id === node.node_id);
      if (
        processedNode === undefined ||
        !('instructions' in processedNode.data) ||
        processedNode.data.instructions === undefined ||
        typeof processedNode.data.instructions === 'string'
      ) {
        return;
      }
      node.connections.forEach((connection, index) => {
        if (!('connection' in connection)) {
          return;
        }
        const instructions = processedNode.data.instructions;
        if (Array.isArray(instructions) && instructions[index] !== undefined) {
          edges.push(
            this.createEdge(node.node_id, connection.connection.next_id, {
              sourceHandle: instructions[index].id, // The length for the instructions array is the same in processedNode and node
            })
          );
        }
      });
    }
    if ('default_connection' in node && node.default_connection !== undefined && node.default_connection !== null) {
      edges.push(
        this.createEdge(node.node_id, node.default_connection.next_id, {
          sourceHandle: 'default_connection-source',
        })
      );
    }
    if ('match_rules' in node) {
      const processedNode: Node<FlowChartNodeData> | undefined = nodes.find((n) => n.id === node.node_id);
      if (
        processedNode === undefined ||
        !('matchRules' in processedNode.data) ||
        processedNode.data.matchRules === undefined
      ) {
        return;
      }
      node.match_rules.forEach((matchRule, index) => {
        if (!('connection' in matchRule)) {
          return;
        }
        const matchRules = processedNode.data.matchRules;
        if (matchRules?.[index] !== undefined) {
          edges.push(
            this.createEdge(node.node_id, matchRule.connection.next_id, {
              sourceHandle: matchRules[index].id, // The length for the matchRules array is the same in processedNode and node
            })
          );
        }
      });
    }
    if ('selections' in node) {
      const processedNode: Node<FlowChartNodeData> | undefined = nodes.find((n) => n.id === node.node_id);
      if (
        processedNode === undefined ||
        !('selections' in processedNode.data) ||
        processedNode.data.selections === undefined
      ) {
        return;
      }
      node.selections.forEach((selection, index) => {
        if (!('connection' in selection)) {
          return;
        }
        const selections = processedNode.data.selections;
        if (selections?.[index] !== undefined) {
          edges.push(
            this.createEdge(node.node_id, selection.connection.next_id, {
              sourceHandle: selections[index].id, // The length for the selections array is the same in processedNode and node
            })
          );
        }
      });
    }
    if ('true_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.true_connection.next_id, {
          sourceHandle: 'true_connection',
        })
      );
    }
    if ('false_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.false_connection.next_id, {
          sourceHandle: 'false_connection',
        })
      );
    }
    if ('available_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.available_connection.next_id, {
          sourceHandle: 'available_connection',
        })
      );
    }
    if ('away_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.away_connection.next_id, {
          sourceHandle: 'away_connection',
        })
      );
    }
    if ('busy_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.busy_connection.next_id, {
          sourceHandle: 'busy_connection',
        })
      );
    }
    if ('timeout_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.timeout_connection.next_id, {
          sourceHandle: 'timeout_connection',
        })
      );
    }
    if ('unreachable_connection' in node) {
      edges.push(
        this.createEdge(node.node_id, node.unreachable_connection.next_id, {
          sourceHandle: 'unreachable_connection',
        })
      );
    }
  }
}
